import {
  Autocomplete,
  Box,
  CircularProgress,
  LinearProgress,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import type { CreditType } from '@vy/api-definition';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCreditTypesList } from '../../../../../../hooks/requests/use-credit-types-list';
import { useDialog } from '../../../../../../hooks/use-dialog';
import { UpsertCreditTypeDialog } from '../../../../../products/ProductsPage/products-page-components/CreateProductDialog/UpsertCreditTypeDialog';
import type { AddCreditForm } from '../add-credit-form/add-credit-form-schema';

const CREATE_NEW_SENTINEL = '__create_new__' as const;

type AutocompleteOption = CreditType | typeof CREATE_NEW_SENTINEL;

export const CreditTypeAutocomplete: React.FC = () => {
  const {
    setValue,
    setError,
    clearErrors,
    watch,
    getValues,
    formState: { errors },
  } = useFormContext<AddCreditForm>();

  const [inputValue, setInputValue] = useState(() => getValues('name') ?? '');
  const {
    isDialogOpen: isCreateDialogOpen,
    handleOpen: handleOpenCreateDialog,
    handleClose: handleCloseCreateDialog,
  } = useDialog();

  const { data, isLoading } = useCreditTypesList({ suspense: false });
  const creditTypes = data?.creditTypes ?? [];

  const filteredCreditTypes = useMemo(() => {
    if (!inputValue) return creditTypes;
    const lower = inputValue.toLowerCase();
    return creditTypes.filter((ct) => ct.name.toLowerCase().includes(lower));
  }, [creditTypes, inputValue]);

  const options: AutocompleteOption[] = [...filteredCreditTypes, CREATE_NEW_SENTINEL];

  const selectedCreditTypeId = watch('creditTypeId');
  const selectedOption = creditTypes.find((creditType) => creditType.id === selectedCreditTypeId) ?? null;

  const hasStaleCreditType = !isLoading && Boolean(selectedCreditTypeId) && selectedOption === null;

  useEffect(() => {
    if (hasStaleCreditType) {
      setError('creditTypeId', {
        type: 'stale',
        message: 'Selected credit type no longer exists in the catalog',
      });
    } else if (errors.creditTypeId?.type === 'stale') {
      clearErrors('creditTypeId');
    }
  }, [hasStaleCreditType, setError, clearErrors, errors.creditTypeId?.type]);

  const handleOptionSelect = (_event: React.SyntheticEvent, option: AutocompleteOption | null) => {
    if (option === null) {
      setValue('creditTypeId', '', { shouldDirty: true, shouldValidate: true });
      setValue('name', '', { shouldDirty: true });
      setInputValue('');
      return;
    }

    if (option === CREATE_NEW_SENTINEL) {
      handleOpenCreateDialog();
      return;
    }

    setValue('creditTypeId', option.id, { shouldDirty: true, shouldValidate: true });
    setValue('name', option.name, { shouldDirty: true, shouldValidate: true });
    setInputValue(option.name);
  };

  const handleCreditTypeCreated = (creditType: CreditType) => {
    setValue('creditTypeId', creditType.id, { shouldDirty: true, shouldValidate: true });
    setValue('name', creditType.name, { shouldDirty: true, shouldValidate: true });
    setInputValue(creditType.name);
  };

  return (
    <>
      <Autocomplete<AutocompleteOption, false, false, false>
        options={options}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={(_event, newValue, reason) => {
          // Do not clear the typed value when an option is selected — only on explicit clear.
          if (reason !== 'reset') {
            setInputValue(newValue);
          }
        }}
        onChange={handleOptionSelect}
        loading={isLoading}
        getOptionLabel={(option) => {
          if (option === CREATE_NEW_SENTINEL) return '';
          return option.name;
        }}
        isOptionEqualToValue={(option, value) => {
          if (option === CREATE_NEW_SENTINEL || value === CREATE_NEW_SENTINEL) return false;
          return option.id === value.id;
        }}
        filterOptions={(opts) => opts} // filtering is done via useMemo above
        renderOption={(props, option) => {
          const { key, ...listItemProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;

          if (option === CREATE_NEW_SENTINEL) {
            return (
              <ListItem
                key={key}
                {...listItemProps}
                onClick={handleOpenCreateDialog}
                data-vy="add-credit-drawer-credit-type-create-new"
                sx={{ borderTop: '1px solid', borderColor: 'divider', cursor: 'pointer' }}
              >
                <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                  + Add credit product
                </Typography>
              </ListItem>
            );
          }

          return (
            <ListItem
              key={key}
              {...listItemProps}
              data-vy={`add-credit-drawer-credit-type-option-${option.id}`}
            >
              <ListItemText
                primary={option.name}
                secondary={
                  <Box component="span" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                    {option.units}
                  </Box>
                }
              />
            </ListItem>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Credit name"
            placeholder="Search or create a credit product"
            data-vy="add-credit-drawer-credit-type-input"
            error={errors.creditTypeId !== undefined || hasStaleCreditType}
            helperText={
              errors.creditTypeId?.message ??
              (hasStaleCreditType ? 'Selected credit type no longer exists in the catalog' : undefined)
            }
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        loadingText={<LinearProgress />}
        noOptionsText="No credit products found"
      />

      <UpsertCreditTypeDialog
        openCreateProductDialog={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        onCreditTypeCreated={handleCreditTypeCreated}
      />
    </>
  );
};
